"use client"

import { APP_URL, BOOK_DEMO_URL } from "../../config"
import type { FullTranslation } from "../../lib/i18n"
import { useAutoleadsLocale } from "../../lib/locale-context"
import {
  ANNUAL_DISCOUNT_PCT,
  POPULAR_TIER,
  TIERS,
  TIER_FEATURES,
  formatEuros,
} from "../../lib/pricing"
import { useTr } from "../../lib/use-tr"
import { LocaleLink } from "../locale-link"
import { PricingComparison } from "../pricing-comparison"
import { Reveal } from "../reveal"
import { TierFeatureList } from "../tier-feature-list"
import {
  ArrowRight,
  CalendarClock,
  Clock,
  Coins,
  Gift,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react"
import { useState } from "react"

import { buildRedirectUrl } from "@/lib/new-url"
import { cn } from "@/lib/utils"

import { Badge } from "@/components/ui/badge"
import { BorderBeam } from "@/components/ui/border-beam"
import { Button } from "@/components/ui/button"

import type { AutoLeadsBillingInterval } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

const BEAM_FROM = "oklch(0.6171 0.1375 39.0427)"
const BEAM_TO = "oklch(0.82 0.11 55)"

const BILLING_INTERVALS = [
  { value: "month", labelKey: "tab_monthly" },
  { value: "year", labelKey: "tab_annual" },
] as const satisfies readonly {
  value: AutoLeadsBillingInterval
  labelKey: string
}[]

const INTERVAL_BUTTON_CLASS =
  "text-foreground/60 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:text-muted-foreground dark:hover:text-foreground relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-[3px] focus-visible:outline-1"

const PRICING_T = {
  eyebrow: { FR: "Tarification", EN: "Pricing" },
  headline: {
    FR: "Des offres simples. Les crédits sont inclus.",
    EN: "Simple plans. Credits included.",
  },
  subhead: {
    FR: "Trois offres, un montant de crédits mensuels, zéro surprise. Dépensez vos crédits sur les enrichissements que vous activez. Résiliable à tout moment.",
    EN: "Three plans, a monthly credit allowance, no surprises. Spend your credits on the enrichments you enable. Cancel anytime.",
  },

  row1_title: { FR: "Regardez avant de payer", EN: "Look before you pay" },
  row1_body: {
    FR: "À l'inscription, une campagne de démonstration vous attend. Aucune carte demandée.",
    EN: "On signup, one demo campaign is waiting. No card required.",
  },

  row2_title: {
    FR: "Vous payez vos enrichissements",
    EN: "You pay for your enrichments",
  },
  row2_body: {
    FR: "Chaque enrichissement que vous activez consomme des crédits. La calculatrice affiche le coût exact avant que vous lanciez la campagne.",
    EN: "Each enrichment you enable consumes credits. The calculator shows the exact cost before you launch the campaign.",
  },

  row3_title: { FR: "Sans engagement", EN: "No lock-in" },
  row3_body: {
    FR: "Changez d'offre ou résiliez quand vous voulez. Vos crédits sont versés chaque mois et facturés mensuellement.",
    EN: "Upgrade, downgrade or cancel whenever you want. Your credits are granted every month and billed monthly.",
  },

  tier_per_month: { FR: "/ mois", EN: "/ mo" },
  tier_ht: { FR: "HT", EN: "excl. tax" },
  tier_credits: { FR: "crédits / mois", EN: "credits / mo" },
  tier_cta: { FR: "Choisir", EN: "Choose" },
  tier_popular: { FR: "Le plus populaire", EN: "Most popular" },
  tab_monthly: { FR: "Mensuel", EN: "Monthly" },
  tab_annual: { FR: "Annuel", EN: "Annual" },
  interval_group_label: {
    FR: "Période de facturation",
    EN: "Billing interval",
  },
  annual_billed_note: {
    FR: "Prix par mois, facturé annuellement.",
    EN: "Price per month, billed yearly.",
  },

  cta_headline: {
    FR: "Explorez les campagnes de démonstration. Sans carte.",
    EN: "Explore the demo campaigns. No card.",
  },
  cta_subhead: {
    FR: "Voyez de vrais prospects enrichis, source par source. Puis choisissez une offre pour lancer la vôtre.",
    EN: "See real enriched leads, source by source. Then pick a plan to launch your own.",
  },
  cta_primary: { FR: "Réserver une démo", EN: "Book a demo" },
  cta_secondary: { FR: "Créer un compte", EN: "Create an account" },
  proof_credits: { FR: "Démos en accès libre", EN: "Demo campaigns included" },
  proof_no_card: {
    FR: "Sans CB, sans engagement",
    EN: "No card, no commitment",
  },
  proof_minutes: {
    FR: "Première campagne en quelques minutes",
    EN: "First campaign in minutes",
  },
} satisfies FullTranslation

type PricingRow = {
  icon: typeof Gift
  titleKey: keyof typeof PRICING_T
  bodyKey: keyof typeof PRICING_T
}

const ROWS: PricingRow[] = [
  { icon: Gift, titleKey: "row1_title", bodyKey: "row1_body" },
  { icon: Coins, titleKey: "row2_title", bodyKey: "row2_body" },
  { icon: TrendingUp, titleKey: "row3_title", bodyKey: "row3_body" },
]

const PROOF_BADGES = [
  { key: "proof_credits", Icon: Gift },
  { key: "proof_no_card", Icon: ShieldCheck },
  { key: "proof_minutes", Icon: Clock },
] as const

// Highlight the middle tier as the recommended plan.

export function Pricing() {
  const tr = useTr(PRICING_T)
  const locale = useAutoleadsLocale()
  const [interval, setInterval] = useState<AutoLeadsBillingInterval>("month")
  const isAnnual = interval === "year"

  return (
    <section
      id="pricing"
      aria-labelledby="pricing-heading"
      className="scroll-mt-28 py-16 sm:py-20 md:py-24 lg:scroll-mt-40"
    >
      <div className="sm:container">
        <Reveal className="mb-12 max-w-2xl text-left">
          <div className="bg-card/60 text-muted-foreground mb-5 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <Sparkles className="text-primary-cta size-3.5" aria-hidden />
            <span>{tr("eyebrow")}</span>
          </div>
          <h2
            id="pricing-heading"
            className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold sm:text-4xl md:text-5xl"
          >
            {tr("headline")}
          </h2>
          <p className="text-muted-foreground text-md mt-4 max-w-md leading-relaxed sm:text-base">
            {tr("subhead")}
          </p>
        </Reveal>

        <Reveal className="-mt-4 mb-4 flex justify-end pr-3 sm:pr-0">
          <div
            role="group"
            aria-label={tr("interval_group_label")}
            className="bg-muted text-muted-foreground relative inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]"
          >
            {BILLING_INTERVALS.map(({ value, labelKey }) => (
              <button
                key={value}
                type="button"
                aria-pressed={interval === value}
                onClick={() => setInterval(value)}
                className={cn(INTERVAL_BUTTON_CLASS, {
                  "bg-background text-foreground dark:border-input dark:bg-input/30 shadow-sm":
                    interval === value,
                })}
              >
                {tr(labelKey)}
              </button>
            ))}
            <Badge
              variant="cta"
              className="absolute -top-3 -right-2 rotate-12 py-0.5 font-semibold tabular-nums sm:-right-6"
            >
              -{ANNUAL_DISCOUNT_PCT}%
            </Badge>
          </div>
        </Reveal>

        <div className="grid items-start gap-5 sm:grid-cols-3 sm:gap-4">
          {TIERS.map((tier) => {
            const popular = tier.tier === POPULAR_TIER
            const ctaLabel = `${tr("tier_cta")} ${tier.label}`
            const displayPrice = isAnnual
              ? tier.annualMonthlyEquivEur
              : tier.priceEur
            return (
              <div
                key={tier.tier}
                className={cn(
                  "relative flex flex-col rounded-2xl border p-6 transition-shadow",
                  popular
                    ? "border-primary-cta/40 ring-primary-cta/30 bg-card shadow-primary-cta/5 z-10 shadow-lg ring-2 sm:-my-2 sm:p-7"
                    : "bg-card/50 ring-border/60 ring-1 hover:shadow-md",
                )}
              >
                {popular ? (
                  <span className="bg-primary-cta text-primary-cta-foreground text-2xs absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full px-3 py-0.5 font-semibold tracking-wide whitespace-nowrap">
                    <Sparkles className="size-3" aria-hidden />
                    {tr("tier_popular")}
                  </span>
                ) : null}

                <div className="text-foreground font-display tracking-dense text-lg font-semibold">
                  {tier.label}
                </div>

                <div className="mt-3 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                  {isAnnual ? (
                    <span className="text-muted-foreground font-display text-xl font-normal tabular-nums line-through">
                      {formatEuros(tier.priceEur, 0)}
                    </span>
                  ) : null}
                  <span className="text-foreground font-display tracking-dense text-4xl font-semibold tabular-nums">
                    {formatEuros(displayPrice, 0)}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {tr("tier_per_month")}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {tr("tier_ht")}
                  </span>
                </div>
                {isAnnual ? (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {tr("annual_billed_note")}
                  </p>
                ) : null}

                <div className="border-border/60 text-muted-foreground mt-4 border-t pt-4 text-sm">
                  <span className="text-foreground font-semibold tabular-nums">
                    {tier.credits.toLocaleString("fr-FR")}
                  </span>{" "}
                  {tr("tier_credits")}
                </div>

                <TierFeatureList
                  features={TIER_FEATURES[tier.tier][locale]}
                  size="md"
                  className="mt-4"
                />

                <LocaleLink
                  href={buildRedirectUrl(
                    APP_URL,
                    `/logged-in/billing?tier=${tier.tier}&interval=${interval}`,
                  )}
                  className="mt-6"
                >
                  <Button
                    shape="lg"
                    variant={popular ? "cta" : "card"}
                    className={cn(
                      "group h-11 w-full text-sm font-medium",
                      popular ? "cta-shine cta-glow" : "cta-shine",
                    )}
                  >
                    {ctaLabel}
                    <ArrowRight
                      className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Button>
                </LocaleLink>
              </div>
            )
          })}
        </div>

        <PricingComparison />

        <div className="mt-12 grid gap-x-8 gap-y-4 sm:grid-cols-3">
          {ROWS.map(({ icon: Icon, titleKey, bodyKey }) => (
            <div key={titleKey} className="flex items-start gap-3">
              <div className="bg-primary-cta/10 flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Icon className="text-primary-cta size-4.5" aria-hidden />
              </div>
              <div>
                <h3 className="text-foreground tracking-slight text-sm font-semibold">
                  {tr(titleKey)}
                </h3>
                <p className="text-muted-foreground text-2sm mt-1 leading-relaxed">
                  {tr(bodyKey)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-primary-cta/15 bg-primary-cta/5 relative mt-12 overflow-hidden rounded-3xl border px-6 py-12 sm:px-12 sm:py-16">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.88 0.09 50 / 0.35), transparent 70%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] opacity-10"
            style={{
              backgroundImage:
                "linear-gradient(to right, oklch(0.5 0 0 / 0.5) 1px, transparent 1px), linear-gradient(to bottom, oklch(0.5 0 0 / 0.5) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          <BorderBeam
            size={160}
            duration={8}
            colorFrom={BEAM_FROM}
            colorTo={BEAM_TO}
          />
          <BorderBeam
            size={160}
            duration={8}
            delay={4}
            colorFrom={BEAM_FROM}
            colorTo={BEAM_TO}
          />

          <div className="relative max-w-2xl">
            <h3 className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold text-balance sm:text-4xl md:text-5xl">
              {tr("cta_headline")}
            </h3>
            <p className="text-muted-foreground text-md mt-5 max-w-xl leading-relaxed text-balance sm:text-base">
              {tr("cta_subhead")}
            </p>

            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  variant="cta"
                  shape="lg"
                  size="lg"
                  className="cta-shine cta-glow h-11 w-full px-6 text-sm font-medium sm:w-auto"
                >
                  <CalendarClock className="mr-1.5 size-4" aria-hidden />
                  {tr("cta_primary")}
                </Button>
              </a>
              <LocaleLink href={APP_URL} className="w-full sm:w-auto">
                <Button
                  shape="lg"
                  variant="soft"
                  size="lg"
                  className="group h-11 w-full px-6 text-sm font-medium sm:w-auto"
                >
                  {tr("cta_secondary")}
                  <ArrowRight
                    className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </Button>
              </LocaleLink>
            </div>

            <div className="text-muted-foreground sm:text-2sm mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              {PROOF_BADGES.map(({ key, Icon }, idx) => (
                <div key={key} className="flex items-center gap-4">
                  {idx > 0 ? (
                    <span
                      aria-hidden
                      className="bg-border/80 hidden h-3 w-px sm:block"
                    />
                  ) : null}
                  <span className="flex items-center gap-1.5">
                    <Icon
                      className="text-primary-cta/70 size-3.5"
                      aria-hidden
                    />
                    <span>{tr(key)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
